<script setup lang="ts">
import $ from 'jquery';
import { computed, onBeforeUnmount, onMounted, ref, unref, watch } from 'vue';

import { WEBAPP_TYPE } from '#public/commons-global';
import { SideNav } from '#public_ts/components';
import { useMainMenuState } from '#public_ts/composables';

import Fattapp2BottomNav from './fattapp2-bottom-nav/bottom-nav.vue';
import MenuMultiPanel from './menu-multi-panel.vue';
import MenuSearchBar from './menu-search-bar.vue';
import UserMenuPanel from './user-menu-panel.vue';
import { DummyUserPanel, useLayoutManagement, useProvideGlobalSearch } from './util';

const ROOT = __ROOT__;
const safari = !!/iPhone|iPad|iPod/i.exec(window.navigator.platform);
const { showCompactInterface } = useLayoutManagement();

const {
    userPanel,
    flattenPanels,
    haveFattapp2Layout,
} = useProvideGlobalSearch(showCompactInterface);
const autoUserPanel = computed(() => unref(userPanel) ?? DummyUserPanel);
const sideNavOpen = ref(false);
const { isMounted, setMounted, setCompact, setFattapp2Active, setMenuPanes } = useMainMenuState();

const bottomNav = computed(() => {
    switch(__CUSTOM_WEB_APPLICATION__) {
        case WEBAPP_TYPE.FATTAPP_V2:
            return Fattapp2BottomNav;
        default:
            return undefined;
    }
});

watch(showCompactInterface, (val) => {
    if(val) sideNavOpen.value = false;
    if(val) {
        document.body.classList.add('menu-layout-mobile');
        document.body.classList.remove('menu-layout-desktop');
    } else {
        document.body.classList.remove('menu-layout-mobile');
        document.body.classList.add('menu-layout-desktop');
    }
    if(isMounted()) {
        setCompact(val);
    }
}, { immediate: true });

watch(haveFattapp2Layout, (val) => {
    if(isMounted()) {
        setFattapp2Active(val);
    }
});

onMounted(() => {
    $(() => {
        setMenuPanes(unref(flattenPanels));
        setMounted(true);
        setCompact(unref(showCompactInterface));
        setFattapp2Active(unref(haveFattapp2Layout));
    });
});

onBeforeUnmount(() => {
    setFattapp2Active(false);
    setMounted(false);
});
</script>

<template>
    <Teleport v-if="bottomNav" to="#mobile-bottom-nav">
        <div class="mobile-bottom-nav-placeholder">
            <component :is="bottomNav" />
        </div>
    </Teleport>

    <div class="menu-block">
        <div class="sidebar-nav">
            <div class="navbar navbar-default" role="navigation">
                <div class="navbar-header">
                    <button
                        type="button"
                        class="navbar-toggle"
                        @click="sideNavOpen = true"
                    >
                        <span class="sr-only">Toggle navigation</span>
                        <i class="bi bi-list" />
                    </button>
                    <div class="navbar-header-logo">
                        <a :href="`${ROOT}Enrollment/index.shtml`" class="header-search-form-compact-logo" />
                    </div>

                    <div id="sidenav-mount" />
                    <UserMenuPanel v-if="showCompactInterface" :panel="autoUserPanel" compact />

                    <div id="mobile-additional-info-mount" />
                </div>
                <SideNav
                    v-if="showCompactInterface"
                    v-model="sideNavOpen"
                    side="left"
                    title="FattApp Menu"
                    side-nav-class="sidebar-navbar-collapse"
                >
                    <ul id="menu-panels" class="nav navbar-nav" :class="{'menu-panels-safari-fix': safari}">
                        <MenuSearchBar />
                        <MenuMultiPanel />
                    </ul>
                </SideNav>
                <div v-else class="navbar-collapse sidebar-navbar-collapse">
                    <ul id="menu-panels" class="nav navbar-nav" :class="{'menu-panels-safari-fix': safari}">
                        <MenuSearchBar />
                        <MenuMultiPanel />
                    </ul>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
.menu-panels-safari-fix {
    max-height: calc(100vh - 145px);
}
#sidenav-mount {
    float: right;
}
#mobile-additional-info-mount {
    display: none;
}
body.menu-layout-mobile #mobile-additional-info-mount {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
</style>
